import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Plug, Loader2, Lock, KeyRound, Copy, Trash2, RotateCw, Eye, EyeOff, ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Settings → API & Integrations
 *
 * Pro / Team gated. Lets the user:
 *   • Create personal API tokens (token shown ONCE on creation).
 *   • Revoke tokens.
 *   • View / copy / rotate the per-org webhook signing secret.
 *   • Copy the public webhook URLs for the generic + WooCommerce receivers.
 */
export default function ApiIntegrationsSection() {
    const { currentOrg, memberships } = useAuth();
    const plan = currentOrg?.plan || "free";
    const proOrTeam = plan === "pro" || plan === "team";
    const role = (memberships || []).find((m) => m.org_id === currentOrg?.id)?.role;
    const canManage = role === "Owner" || role === "Admin";

    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [newScope, setNewScope] = useState("read");
    const [revealed, setRevealed] = useState(null); // { name, token, scope }
    const [showSecret, setShowSecret] = useState(false);
    const [secretInfo, setSecretInfo] = useState(null); // { secret, woocommerce_url, generic_url }

    const refreshKeys = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/api-keys");
            setKeys(data.items || []);
        } catch (e) {
            // Plan gate — just show empty.
            setKeys([]);
        } finally { setLoading(false); }
    };

    const refreshSecret = async () => {
        try {
            const { data } = await api.get("/webhook-secret");
            // If backend returned a relative URL (no scheme), prepend the
            // browser origin so the copied URL works out-of-the-box.
            const origin = typeof window !== "undefined" ? window.location.origin : "";
            const absolutise = (u) => (u && /^https?:\/\//.test(u) ? u : `${origin}${u || ""}`);
            setSecretInfo({
                ...data,
                generic_url: absolutise(data.generic_url),
                woocommerce_url: absolutise(data.woocommerce_url),
                shopify_url: absolutise(data.shopify_url),
            });
        } catch (e) {
            setSecretInfo(null);
        }
    };

    useEffect(() => {
        if (currentOrg && proOrTeam && canManage) {
            refreshKeys();
            refreshSecret();
        }
    }, [currentOrg?.id, plan]);

    const createKey = async () => {
        if (!newName.trim()) {
            toast.error("Give your token a memorable name");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/api-keys", { name: newName.trim(), scope: newScope });
            setRevealed({ name: data.name, token: data.token, scope: data.scope });
            setNewName("");
            setNewScope("read");
            setCreateOpen(false);
            await refreshKeys();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const revokeKey = async (k) => {
        if (!window.confirm(`Revoke "${k.name}"? Any integration using this token will stop working immediately.`)) return;
        try {
            await api.delete(`/api-keys/${k.id}`);
            toast.success("Token revoked");
            await refreshKeys();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const rotateSecret = async () => {
        if (!window.confirm("Rotate the webhook signing secret? You'll need to update WooCommerce / your integration with the new secret.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/webhook-secret/rotate", {});
            setSecretInfo((prev) => ({ ...(prev || {}), secret: data.secret }));
            toast.success("Webhook secret rotated");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const copy = async (text, label = "Copied") => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(label);
        } catch { toast.error("Couldn't copy — long-press to select"); }
    };

    return (
        <section
            className="rounded-3xl bg-white border border-sand-200 shadow-card p-6 lg:col-span-2"
            data-testid="api-integrations-section"
        >
            <div className="flex items-center gap-2">
                <Plug className="h-5 w-5 text-coral" />
                <h3 className="font-heading text-xl text-ocean-700">API & Integrations</h3>
                {proOrTeam ? (
                    <Badge className="bg-secondary/10 text-secondary-dark border-0">{plan === "team" ? "Team" : "Pro"} plan</Badge>
                ) : (
                    <Badge className="bg-sand-200 text-ocean-700 border-0">Pro &amp; Team only</Badge>
                )}
            </div>

            <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
                Connect WooCommerce, Shopify, Zapier, or any custom tool. Webhook orders auto-create a paid invoice in BlueLedger. Use a personal token to read your data programmatically.
            </p>

            {!proOrTeam && (
                <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-sand-100 text-ocean-700 text-sm px-3 py-2" data-testid="api-upgrade-nudge">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <Link to="/app/billing" className="hover:underline">Upgrade to Pro</Link>
                    <span className="text-muted-foreground">to unlock the API and webhooks.</span>
                </div>
            )}

            {proOrTeam && !canManage && (
                <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-sand-100 text-ocean-700 text-sm px-3 py-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    Owners and Admins manage API keys.
                </div>
            )}

            {proOrTeam && canManage && (
                <div className="mt-6 space-y-7">
                    {/* ----- Personal API tokens ----- */}
                    <div>
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <KeyRound className="h-4 w-4 text-coral" />
                                    <h4 className="font-heading text-sm text-ocean-700">Personal API tokens</h4>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">Used as <code className="font-mono text-[11px]">Authorization: Bearer ...</code>. Shown once at creation.</p>
                            </div>
                            <Button
                                onClick={() => setCreateOpen(true)}
                                size="sm"
                                className="bg-ocean-700 text-white rounded-xl"
                                data-testid="api-key-create-btn"
                            >
                                + New token
                            </Button>
                        </div>

                        <div className="rounded-2xl border border-sand-200 overflow-hidden" data-testid="api-keys-list">
                            {loading ? (
                                <div className="p-6 text-sm text-muted-foreground text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
                            ) : keys.length === 0 ? (
                                <div className="p-6 text-sm text-muted-foreground text-center">No tokens yet. Create one to start integrating.</div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="bg-sand-50 text-xs text-muted-foreground">
                                        <tr>
                                            <th className="text-left p-3 font-medium">Name</th>
                                            <th className="text-left p-3 font-medium">Scope</th>
                                            <th className="text-left p-3 font-medium">Token</th>
                                            <th className="text-left p-3 font-medium">Created</th>
                                            <th className="text-left p-3 font-medium">Last used</th>
                                            <th className="text-right p-3 font-medium">Status</th>
                                            <th className="p-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {keys.map((k) => (
                                            <tr key={k.id} className="border-t border-sand-200" data-testid={`api-key-row-${k.id}`}>
                                                <td className="p-3 font-medium text-ocean-700">{k.name}</td>
                                                <td className="p-3"><ScopeBadge scope={k.scope || "read"} /></td>
                                                <td className="p-3 font-mono text-xs text-muted-foreground">blk_live_•••• {k.last4}</td>
                                                <td className="p-3 text-xs text-muted-foreground">{k.created_at?.slice(0, 10)}</td>
                                                <td className="p-3 text-xs text-muted-foreground">{k.last_used_at ? k.last_used_at.slice(0, 10) : "Never"}</td>
                                                <td className="p-3 text-right">
                                                    {k.revoked ? (
                                                        <Badge className="bg-coral/10 text-coral-dark border-0">Revoked</Badge>
                                                    ) : (
                                                        <Badge className="bg-secondary/10 text-secondary-dark border-0">Active</Badge>
                                                    )}
                                                </td>
                                                <td className="p-3 text-right">
                                                    {!k.revoked && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-coral-dark hover:bg-coral/10"
                                                            onClick={() => revokeKey(k)}
                                                            data-testid={`api-key-revoke-${k.id}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* ----- Webhook secret + URLs ----- */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Plug className="h-4 w-4 text-coral" />
                            <h4 className="font-heading text-sm text-ocean-700">Webhook signing secret</h4>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            Configure WooCommerce or your custom system with this secret + URL. Every incoming webhook is HMAC-verified before any invoice is created.
                        </p>
                        <div className="mt-3 grid sm:grid-cols-[1fr,auto,auto] items-center gap-2">
                            <Input
                                readOnly
                                type={showSecret ? "text" : "password"}
                                value={secretInfo?.secret || ""}
                                className="font-mono text-xs"
                                data-testid="webhook-secret-input"
                            />
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setShowSecret((v) => !v)}
                                className="rounded-xl border-sand-300"
                                data-testid="webhook-secret-toggle"
                                title={showSecret ? "Hide" : "Reveal"}
                            >
                                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => copy(secretInfo?.secret || "", "Webhook secret copied")}
                                    className="rounded-xl border-sand-300"
                                    data-testid="webhook-secret-copy"
                                    disabled={!secretInfo?.secret}
                                >
                                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={rotateSecret}
                                    disabled={busy}
                                    className="rounded-xl border-sand-300"
                                    data-testid="webhook-secret-rotate"
                                >
                                    <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Rotate
                                </Button>
                            </div>
                        </div>

                        <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="webhook-urls">
                            <UrlCard
                                title="WooCommerce delivery URL"
                                hint='Topic: "Order updated" · Status: Active'
                                url={secretInfo?.woocommerce_url}
                                onCopy={(v) => copy(v, "WooCommerce URL copied")}
                                testid="webhook-url-woo"
                            />
                            <UrlCard
                                title="Shopify delivery URL"
                                hint='Topic: "Order paid" · Format: JSON'
                                url={secretInfo?.shopify_url}
                                onCopy={(v) => copy(v, "Shopify URL copied")}
                                testid="webhook-url-shopify"
                            />
                            <UrlCard
                                title="Generic webhook URL"
                                hint="POST JSON · X-BL-Signature = base64 HMAC-SHA256(body)"
                                url={secretInfo?.generic_url}
                                onCopy={(v) => copy(v, "Generic URL copied")}
                                testid="webhook-url-generic"
                            />
                        </div>

                        <div className="mt-4 grid md:grid-cols-2 gap-3 text-xs text-muted-foreground">
                            <div className="rounded-2xl bg-sand-50 border border-sand-200 p-4 space-y-1.5">
                                <div className="font-medium text-ocean-700 text-sm">Quick start with WooCommerce</div>
                                <ol className="list-decimal pl-4 space-y-0.5">
                                    <li>WP Admin → WooCommerce → Settings → Advanced → <strong>Webhooks</strong> → <strong>Add webhook</strong>.</li>
                                    <li>Topic: <strong>Order updated</strong> (or <em>Order created</em>). Status: <strong>Active</strong>.</li>
                                    <li>Delivery URL: paste the WooCommerce URL above.</li>
                                    <li>Secret: paste the secret above.</li>
                                    <li>Save. Completed orders become paid invoices in BlueLedger.</li>
                                </ol>
                                <a
                                    href="https://woocommerce.com/document/webhooks/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-coral-dark hover:underline mt-1"
                                >
                                    WooCommerce docs <ExternalLink className="h-3 w-3" />
                                </a>
                            </div>
                            <div className="rounded-2xl bg-sand-50 border border-sand-200 p-4 space-y-1.5">
                                <div className="font-medium text-ocean-700 text-sm">Quick start with Shopify</div>
                                <ol className="list-decimal pl-4 space-y-0.5">
                                    <li>Shopify admin → <strong>Settings → Notifications → Webhooks</strong> → <strong>Create webhook</strong>.</li>
                                    <li>Event: <strong>Order paid</strong> · Format: <strong>JSON</strong>.</li>
                                    <li>URL: paste the Shopify URL above.</li>
                                    <li>Copy Shopify&apos;s <em>signing secret</em> and paste it above (or vice-versa — rotate the BlueLedger secret to match).</li>
                                    <li>Save. Paid orders become paid invoices in BlueLedger.</li>
                                </ol>
                                <a
                                    href="https://shopify.dev/docs/apps/build/webhooks"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-coral-dark hover:underline mt-1"
                                >
                                    Shopify webhook docs <ExternalLink className="h-3 w-3" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create token dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-md" data-testid="api-key-create-dialog">
                    <DialogHeader><DialogTitle>New API token</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Name</Label>
                            <Input
                                className="mt-1.5"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder="e.g. WooCommerce production"
                                data-testid="api-key-name-input"
                                autoFocus
                            />
                            <p className="text-xs text-muted-foreground mt-1.5">
                                Pick a memorable name — you&apos;ll use this to spot the token in audit logs later.
                            </p>
                        </div>
                        <div>
                            <Label>Access level</Label>
                            <div className="mt-1.5 space-y-2" data-testid="api-key-scope-picker">
                                {[
                                    { value: "read", title: "Read only",
                                      body: "GET /api/v1/* — list & fetch invoices, customers, bills, etc. Recommended for reporting tools and BI dashboards." },
                                    { value: "write", title: "Read & write",
                                      body: "Everything Read can do, plus create / edit / record payments. Recommended for automation tools like Zapier." },
                                    { value: "admin", title: "Admin",
                                      body: "Everything above, plus send-email + delete. Use only for trusted internal scripts — a leaked admin token can email your customers." },
                                ].map((opt) => (
                                    <label
                                        key={opt.value}
                                        className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${newScope === opt.value ? "border-secondary bg-secondary/5" : "border-sand-200 hover:bg-sand-50"}`}
                                        data-testid={`api-key-scope-${opt.value}`}
                                    >
                                        <input
                                            type="radio"
                                            name="api-key-scope"
                                            className="mt-1 accent-secondary"
                                            checked={newScope === opt.value}
                                            onChange={() => setNewScope(opt.value)}
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 text-sm font-medium text-ocean-700">
                                                {opt.title}
                                                <ScopeBadge scope={opt.value} />
                                            </div>
                                            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                                                {opt.body}
                                            </p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1.5">
                                Requests are rate-limited per token: 120/min for reads, 60/min for writes.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <Button
                            onClick={createKey}
                            disabled={busy || !newName.trim()}
                            className="bg-ocean-700 text-white rounded-xl"
                            data-testid="api-key-create-confirm"
                        >
                            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                            Create token
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reveal token (one-shot) */}
            <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
                <DialogContent className="max-w-lg" data-testid="api-key-reveal-dialog">
                    <DialogHeader><DialogTitle>Save your new token</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-xl border-2 border-coral/40 bg-coral/5 p-3 text-sm text-ocean-700">
                            <strong>This is the only time we&apos;ll show this token.</strong> Copy it now and paste it into your integration. If you lose it, just revoke this token and create a new one.
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label>{revealed?.name}</Label>
                                {revealed?.scope && <ScopeBadge scope={revealed.scope} />}
                            </div>
                            <div className="mt-1.5 flex gap-2">
                                <Input
                                    readOnly
                                    value={revealed?.token || ""}
                                    className="font-mono text-xs"
                                    data-testid="api-key-revealed-token"
                                />
                                <Button
                                    variant="outline"
                                    onClick={() => copy(revealed?.token || "", "Token copied")}
                                    className="rounded-xl border-sand-300"
                                    data-testid="api-key-copy-btn"
                                >
                                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                                </Button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={() => setRevealed(null)}
                            className="bg-ocean-700 text-white rounded-xl"
                            data-testid="api-key-reveal-done"
                        >
                            I&apos;ve saved it
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}


function ScopeBadge({ scope }) {
    const styles = {
        read: "bg-sand-200/70 text-ocean-700",
        write: "bg-secondary/10 text-secondary-dark",
        admin: "bg-coral/10 text-coral-dark",
    };
    const cls = styles[scope] || styles.read;
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${cls}`} data-testid={`scope-badge-${scope}`}>
            {scope}
        </span>
    );
}


function UrlCard({ title, hint, url, onCopy, testid }) {    return (
        <div className="rounded-2xl border border-sand-200 p-3" data-testid={testid}>
            <div className="text-xs font-medium text-ocean-700">{title}</div>
            <div className="mt-1.5 flex gap-2">
                <Input
                    readOnly
                    value={url || ""}
                    className="font-mono text-[11px]"
                    data-testid={`${testid}-input`}
                />
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onCopy(url || "")}
                    className="rounded-xl border-sand-300 shrink-0"
                    disabled={!url}
                    data-testid={`${testid}-copy`}
                    title="Copy URL"
                >
                    <Copy className="h-4 w-4" />
                </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{hint}</p>
        </div>
    );
}
